import React from 'react';

const Hero: React.FC = () => {
  return (
    <section
      id="top"
      className="relative min-h-screen overflow-hidden"
      style={{
        background: `
    radial-gradient(
      circle at -8% 108%,
      rgba(170, 48, 8, 0.95) 0%,
      rgba(190, 70, 12, 0.85) 22%,
      rgba(220, 100, 18, 0.55) 42%,
      rgba(245, 130, 28, 0.22) 62%,
      rgba(245, 130, 28, 0.06) 78%,
      transparent 100%
    ),

    radial-gradient(
      circle at 115% -10%,
      rgba(255, 190, 120, 0.18) 0%,
      rgba(255, 190, 120, 0.09) 28%,
      rgba(255, 190, 120, 0.03) 48%,
      transparent 75%
    ),

    linear-gradient(
      90deg,
      #7c210f 0%,
      #c22c07 42%,
      #f9540f 100%
    )
  `,
      }}
    >
      <div className="flex flex-col lg:flex-row min-h-screen items-center pt-16">

        {/* Left: text */}
        <div className="w-full lg:w-1/2 flex items-center pl-[7vw] pr-8 lg:pr-0 py-16 lg:py-0 z-10 order-1 lg:order-1">
          <div>
            <h1
              className="font-extrabold text-white leading-[1.05] tracking-tight mb-4"
              style={{ fontSize: 'clamp(2.6rem, 5vw, 5.5rem)' }}
            >
              Top Up Airtime<br />
              Back Home, Instantly
            </h1>
            <p className="text-white font-semibold mb-5" style={{ fontSize: 'clamp(1.2rem, 2.2vw, 1.9rem)' }}>
              Pay with any debit or credit card
            </p>
            <p className="text-white font-bold text-sm tracking-widest uppercase mb-3">
              Delivered like a text!
            </p>
            <p className="text-white/70 text-sm leading-relaxed" style={{ maxWidth: '360px' }}>
              Buy airtime for loved ones across Africa in seconds. Starting with diaspora communities in the USA sending airtime to Ethiopia, Kenya, Ghana and Nigeria, with more countries coming soon.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#download"
                className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 transition text-white text-sm font-bold tracking-widest px-6 py-3.5 rounded-full"
              >
                DOWNLOAD APP
                <span className="w-7 h-7 bg-white rounded-full flex items-center justify-center text-black text-base">→</span>
              </a>
            </div>
          </div>
        </div>

        {/* Right: floating phone + top-up illustration */}
        <div className="w-full lg:w-1/2 flex items-center justify-center py-10 lg:py-20 order-2 lg:order-2">
          <div className="relative" style={{ width: 'clamp(300px, 40vw, 480px)', aspectRatio: '3 / 4' }}>

            {/* Glow */}
            <div
              className="animate-glow-pulse"
              style={{
                position: 'absolute', left: '50%', top: '50%', width: '90%', height: '90%',
                transform: 'translate(-50%, -50%)', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%)',
                zIndex: 1,
              }}
            />

            {/* Phone frame */}
            <div
              className="animate-float-main"
              style={{
                position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                width: '70%', aspectRatio: '9 / 18.5', zIndex: 10,
                background: '#111', borderRadius: '2.2rem', padding: '10px',
                boxShadow: '0 30px 60px rgba(60, 20, 0, 0.45)',
              }}
            >
              <div
                style={{
                  width: '100%', height: '100%', borderRadius: '1.6rem',
                  background: 'linear-gradient(160deg, #fff7f0 0%, #ffe4d3 100%)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '1.2rem', position: 'relative', overflow: 'hidden',
                }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: '#ea3d05' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <p className="font-extrabold text-gray-900 text-base mb-1">Top-Up Sent!</p>
                <p className="text-gray-500 text-xs mb-4">$10.00 airtime delivered</p>
                <div className="w-full rounded-xl px-3 py-2.5 flex items-center justify-between" style={{ background: 'white', border: '1px solid #ffd9bd' }}>
                  <span className="text-gray-700 text-xs font-semibold flex items-center gap-1.5">
                    <span className="text-[9px] font-black text-white rounded px-1 py-0.5" style={{ background: '#ea3d05' }}>ET</span>
                    +251 91 ••• ••42
                  </span>
                  <span className="text-xs font-bold" style={{ color: '#ea3d05' }}>DONE</span>
                </div>
              </div>
            </div>

            {/* Floating chip: signal bars */}
            <div
              className="animate-float-secondary"
              style={{ position: 'absolute', left: '0%', top: '14%', zIndex: 20 }}
            >
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 shadow-xl">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#ea3d05"><path d="M2 20h3v-6H2v6zm5 0h3V10H7v10zm5 0h3V4h-3v16zm5 0h3v-3h-3v3z"/></svg>
                <span className="text-gray-900 text-xs font-bold">Instant Delivery</span>
              </div>
            </div>

            {/* Floating chip: countries */}
            <div
              className="animate-float-tertiary"
              style={{ position: 'absolute', right: '-2%', top: '58%', zIndex: 20 }}
            >
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 shadow-xl">
                <span className="flex gap-1">
                  {['ET', 'KE', 'GH', 'NG'].map(code => (
                    <span key={code} className="text-[10px] font-black text-white rounded px-1.5 py-1 tracking-wide" style={{ background: '#ea3d05' }}>
                      {code}
                    </span>
                  ))}
                </span>
                <span className="text-gray-900 text-xs font-bold">4 Countries</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
